'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';

import { useForm } from 'react-hook-form';
import { z } from 'zod';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string(),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

type ContactFormInput = z.input<ReturnType<typeof contactSchema>>;
type ContactFormValues = z.output<ReturnType<typeof contactSchema>>;

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '8px',
  border: '1px solid hsl(240 4% 91%)',
  fontSize: '14px',
  fontFamily: 'inherit',
  background: 'hsl(231 47% 97%)',
};

const labelStyle = {
  fontSize: '13px',
  fontWeight: 600,
  display: 'block',
  marginBottom: '6px',
} as const;

const CONTENT = {
  sk: {
    lead: 'Vyplňte formulár a do 24 hodín vám pripravíme ponuku na mieru vrátane kalkulácie návratnosti. Alebo nám jednoducho zavolajte.',
    benefits: [
      'Bezplatná konzultácia a obhliadka pozemku',
      'Cenová ponuka do 24 hodín',
      'Kalkulácia návratnosti oproti žumpe',
      'Poradenstvo ohľadom povolení a dotácií',
    ],
  },
  de: {
    lead: 'Füllen Sie das Formular aus und wir bereiten Ihnen innerhalb von 24 Stunden ein maßgeschneidertes Angebot inklusive Berechnung der Amortisation vor. Oder rufen Sie uns einfach an.',
    benefits: [
      'Kostenlose Beratung und Grundstücksbesichtigung',
      'Angebot innerhalb von 24 Stunden',
      'Berechnung der Amortisation gegenüber der Sickergrube',
      'Beratung zu Genehmigungen und Förderungen',
    ],
  },
};

const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="hsl(90 57% 41%)"
    strokeWidth="2.5"
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);

export function Contact() {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormInput, any, ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({ mutationFn: sendContactMessage });
  const locale = useLocaleStore(s => s.currentLocale);

  const placeholders =
    locale === 'de'
      ? { name: 'Max Mustermann', message: 'Beschreiben Sie Ihre Situation...' }
      : { name: 'Ján Novák', message: 'Opíšte vašu situáciu...' };
  const content = locale === 'de' ? CONTENT.de : CONTENT.sk;

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: { ...data,  },
        locale,
      });

      toast({ title: t('text658'), variant: 'success' });

      if (window.ga) window.ga('send', 'event', 'form', 'submit', 'contact');
      if (window.gtag)
        window.gtag('event', 'submit', { event_category: 'form', event_label: 'contact' });
      if (window.fbq) window.fbq('track', 'Contact');

      form.reset();
    } catch (err) {
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
      toast({ title: t('text657'), description: (err as any)?.message, variant: 'destructive' });
    }
  });

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'clamp(24px,5vw,40px)',
        background: 'linear-gradient(135deg, hsl(90 57% 94%), hsl(90 57% 88%))',
      }}
      className="py-8 sm:py-10 lg:py-12"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', width: '100%' }}>
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
          <div>
            <p
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: 'hsl(90 57% 41%)',
                marginBottom: '8px',
              }}
            >
              {t('text3912')}
            </p>
            <h2
              style={{
                fontSize: 'clamp(1.4rem,3vw,2.2rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                marginBottom: '16px',
              }}
            >
              {t('text3913')}
            </h2>
            <p
              style={{
                fontSize: '15px',
                color: 'hsl(245 6% 45%)',
                lineHeight: 1.7,
                marginBottom: '24px',
              }}
            >
              {content.lead}
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                marginBottom: '24px',
              }}
            >
              {content.benefits.map((item, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '15px' }}
                >
                  <CheckIcon />
                  {item}
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid hsl(240 4% 91%)',
              }}
            >
              <div style={{ fontSize: '28px' }}>📞</div>
              <div>
                <div style={{ fontSize: '12px', color: 'hsl(245 6% 45%)', fontWeight: 600 }}>
                  {t('text3914')}
                </div>
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'hsl(90 57% 30%)' }}>
                  +421 918 742 738
                </div>
                <div style={{ fontSize: '12px', color: 'hsl(245 6% 45%)' }}>
                  {t('text3915')}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '36px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            }}
          >
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 700,
                marginBottom: '24px',
                textAlign: 'center',
              }}
            >
              {t('text3523')}
            </h3>

            <Form {...form}>
              <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={labelStyle}>{t('text1250')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder={placeholders.name}
                          style={inputStyle}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={labelStyle}>{t('text36')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          placeholder="+421 9XX XXX XXX"
                          style={inputStyle}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={labelStyle}>{t('text35')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="jan@email.sk"
                          style={inputStyle}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="text"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={labelStyle}>{t('text615')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={3}
                          placeholder={placeholders.message}
                          style={{ ...inputStyle, resize: 'vertical' }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gdpr"
                  render={({ field }) => (
                    <FormItem className="flex items-start gap-2">
                      <FormControl>
                        <Checkbox
                          ref={field.ref}
                          name={field.name}
                          disabled={field.disabled}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel
                        style={{
                          fontSize: '11px',
                          color: 'hsl(245 6% 45%)',
                          fontWeight: 400,
                          lineHeight: 1.5,
                          cursor: 'pointer',
                        }}
                      >
                        {t('text613')}{' '}
                        <Link href={ROUTES.GDPR()} prefetch={false}>
                          <span className="text-primary">{t('text614')}</span>
                        </Link>
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="g-recaptcha-response"
                  render={() => (
                    <FormItem>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  isLoading={form.formState.isSubmitting}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '12px',
                    background: 'hsl(90 57% 41%)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '15px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t('text3909')}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
}
